import { useState } from 'react';
import { Trans } from 'react-i18next';
import { FormAction } from '@anytag/shared-types';
import { closeIconImage } from '@anytag/shared-assets';
import { CURRENCY_SYMBOLS } from '@anytag/shared-constants';
import { useQueryHelper, useToggle } from '@anytag/shared-hooks';
import { cn, formatNumber, formatPrice } from '@anytag/shared-utils';
import { wait, namedOperations, useRechargeAdvertiserAccountMutation } from '@anytag/graphql';
import { Anchor, BaseModal, Button, Form, Icon, ModalBody, ModalFooter } from '@anytag/shared-atoms';
import { MoneyField } from '../TransferModal/MoneyField';
import { CardInfo } from './CardInfo';
import { getRechargeModalSchema, maskitoParseNumber } from './schema';
import { RechargeModalFormValues, RechargeModalProps } from './types';

export const RechargeModal = ({ prepaidBalance, currencyId, className }: RechargeModalProps) => {
  const { t, enqueueSnackbar, invalidateRouteLoader } = useQueryHelper();
  const [checkoutUrl, setCheckoutUrl] = useState('');
  const { on, setOn, setOff } = useToggle();
  let reset: () => void | undefined;

  const { callRechargeAdvertiserAccount, loading } = useRechargeAdvertiserAccountMutation({
    refetchQueries: [namedOperations.Query.TiktokAdvertiserSummaryData],
    onCompleted: () => {
      wait(500);
      invalidateRouteLoader();
    }
  });

  const handleSubmit: FormAction<RechargeModalFormValues>['onSubmit'] = async ({ amount }, methods) => {
    reset = methods.reset;

    await callRechargeAdvertiserAccount({
      variables: { input: { amount: maskitoParseNumber(amount), redirectUrl: window.location.href } }
    })
      .then(({ data }) => {
        if (data?.rechargeAdvertiserAccount?.ok) {
          // if user billing is OK wi will automatically charge them
          enqueueSnackbar(t('Selected amount recharged successfully'), { variant: 'success' });
          setOff();
          reset();
        } else if (data?.rechargeAdvertiserAccount?.checkoutUrl) {
          // if user has not enough balance or some issue, we need to redirect the to Stripe with callback
          setCheckoutUrl(data.rechargeAdvertiserAccount.checkoutUrl);
        }
      })
      .catch((error) => {
        enqueueSnackbar(error.message, { variant: 'error' });
        reset();
        setOff();
      });
  };

  return (
    <div className={className}>
      <Button
        variant="solid"
        prefixIcon={{ icon: 'plus', size: '11px' }}
        title={t('Button.Recharge')}
        onClick={setOn}
      />
      <BaseModal open={on} onClose={setOff} title={t('Recharge')} asChild>
        <Form.Provider<RechargeModalFormValues>
          onSubmit={handleSubmit}
          zodSchema={getRechargeModalSchema()}
          defaultValues={{ amount: '', step: 'input' }}
        >
          {({ values: { step, amount }, setValue }) => {
            const currencyPrefix = CURRENCY_SYMBOLS[currencyId];
            const unmaskedAmount = amount ? maskitoParseNumber(amount) : 0;

            return (
              <>
                <ModalBody>
                  <div className="pb-4">
                    {step === 'confirm' ? (
                      <>
                        <p className="text-base font-normal">
                          <Trans
                            i18nKey="Are you sure to recharge"
                            values={{ amount: `${amount} ${currencyId}` }}
                            components={{ b: <b className="text-base leading-6 font-semibold" /> }}
                          />
                          <br />
                          <span className="leading-6">{t('This process cannot be undone')}</span>
                        </p>
                        {checkoutUrl ? (
                          <>
                            <div className="my-2.5 flex items-center rounded-sm border-base-red-20 bg-red-10 py-2 pl-2 text-sm">
                              <img src={closeIconImage} className="size-4" alt="red close icon" />
                              <span className="ml-2 text-sm font-normal">
                                {t('The payment has failed Please check your credit card information')}
                              </span>
                            </div>
                            <Anchor href={checkoutUrl} target="_self" variant="blue" className="text-base font-normal">
                              <div className="inline-flex gap-1">
                                <Icon icon="external-link" size={14} variant="blue" className="self-center" />
                                <span>{t('Recheck your Card information')}</span>
                              </div>
                            </Anchor>
                          </>
                        ) : (
                          ''
                        )}
                      </>
                    ) : (
                      <>
                        <MoneyField
                          name="amount"
                          placeholder={formatPrice(10000, null, 0)}
                          triggerFormRevalidation
                          prefix={currencyId ? CURRENCY_SYMBOLS[currencyId] : ''}
                        />
                        <p className="mt-1 mb-2 flex justify-between text-base font-normal text-black-60">
                          <div className="font-semibold">{t('New Prepaid Balance')}</div>
                          <span className="font-semibold text-black-90">
                            {prepaidBalance !== undefined && prepaidBalance !== null
                              ? `${currencyPrefix}${formatNumber(prepaidBalance + unmaskedAmount, 0)}`
                              : '-'}
                          </span>
                        </p>

                        <CardInfo className="p-0 shadow-none" />
                      </>
                    )}
                  </div>
                </ModalBody>

                <ModalFooter
                  cancelButton={
                    step === 'input'
                      ? { onClick: setOff, title: t('Button.Cancel') }
                      : {
                          title: t('Button.Back'),
                          className: cn('min-w-22.5'),
                          onClick: () => setValue('step', 'input')
                        }
                  }
                  submitButton={
                    step === 'input'
                      ? {
                          title: t('Button.Recharge'),
                          disabled: !amount || Number(amount) < 10,
                          onClick: () => setValue('step', 'confirm')
                        }
                      : {
                          type: 'submit',
                          title: t('Button.Sure'),
                          loading: { status: loading, showIcon: true }
                        }
                  }
                />
              </>
            );
          }}
        </Form.Provider>
      </BaseModal>
    </div>
  );
};
